import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Users, Briefcase, TrendingUp, Building2, UserCheck, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <div className="text-sm font-medium text-slate-600">Admin Panel</div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}} data-testid="admin-heading">
              Platform Analytics
            </h1>
            <p className="text-lg text-slate-600">Overview of Sponsiq platform metrics</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/analytics">
              <Button variant="outline" className="border-indigo-200 hover:bg-indigo-50" data-testid="analytics-dashboard-btn">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics Dashboard
              </Button>
            </Link>
            <Link to="/admin/import">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="csv-import-btn">
                CSV Import
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <Card className="p-6" data-testid="total-creators-card">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Creators</p>
                <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{stats?.total_creators || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="claimed-creators-card">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Claimed Profiles</p>
                <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{stats?.claimed_creators || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="total-brands-card">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Brands</p>
                <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{stats?.total_brands || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="waitlist-brands-card">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Brands on Waitlist</p>
                <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{stats?.brands_on_waitlist || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="brand-requests-card">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Brand Requests</p>
                <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{stats?.total_brand_requests || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="engagement-card">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Engagement Rate</p>
                <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                  {stats?.total_creators > 0 
                    ? Math.round((stats.claimed_creators / stats.total_creators) * 100) 
                    : 0}%
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Platform Health */}
        <Card className="p-8">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>Platform Health</h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600">Creator Engagement</span>
                <span className="font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                  {stats?.total_creators > 0 
                    ? Math.round((stats.claimed_creators / stats.total_creators) * 100) 
                    : 0}%
                </span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-indigo-600 rounded-full" 
                  style={{
                    width: `${stats?.total_creators > 0 
                      ? Math.round((stats.claimed_creators / stats.total_creators) * 100) 
                      : 0}%`
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600">Brand Interest</span>
                <span className="font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                  {stats?.brands_on_waitlist || 0} brands
                </span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-600 rounded-full" 
                  style={{width: `${Math.min((stats?.brands_on_waitlist || 0) * 10, 100)}%`}}
                ></div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;
